import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Logo } from '../../components/common/Logo';
import { Loader2 } from 'lucide-react';

export const ValidateCodePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = location.state?.userId;
  const email = location.state?.email;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/core/auth/ChekeCode/${code}/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Invalid code');
      }
      
      const data = await response.json();
      
      if (data.result !== "Codigo correcto") {
        throw new Error('Invalid code');
      }

      // Navigate to reset password page
      navigate('/reset-password', { state: { userId: data.userId, code: data.code } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate code');
    } finally {
      setIsLoading(false);
    }
  };

  if (!userId || !email) {
    return (
      <div className="container mx-auto px-4 py-16 flex flex-col items-center justify-center min-h-screen">
        <div className="w-full max-w-md space-y-8 text-center">
          <Logo />
          <div className="bg-red-500/10 p-4 rounded-lg">
            <p className="text-red-400">Invalid recovery session. Please try again.</p>
            <Link to="/recover-password" className="text-[#5CD2F4] hover:underline mt-4 block">
              Back to Password Recovery
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-16 flex flex-col items-center justify-center h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-[#5CD2F4] animate-spin mx-auto" />
          <p className="text-white text-lg">Validating your code...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16 flex flex-col items-center justify-center h-screen">
      <div className="w-full max-w-md space-y-12">
        <div className="space-y-4 text-center">
          <div className="space-y-2">
            <p className="text-lg text-white font-montserrat font-medium">
              Enter Recovery Code
            </p>
            <p className="text-sm text-gray-400 font-montserrat font-light">
              Please enter the code sent to {email}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter recovery code..."
            className="input-field"
            required
            disabled={isLoading}
          />
          
          {error && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}
          
          <div className="space-y-3">
            <button
              type="submit"
              className="w-full px-4 py-3 bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] text-gray-900 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Validating...
                </>
              ) : (
                'Verify Code'
              )}
            </button>
            
            <Link
              to="/recover-password"
              className="block text-center text-sm text-[#5CD2F4] hover:underline"
            >
              Back to Password Recovery
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};